import { html, render } from 'lit-html';
import { createRef, ref, Ref } from 'lit-html/directives/ref.js';
import { authService } from './authService.js';

export class AuthOverlay extends HTMLElement {
  private shouldRenderGoogleSignInButton = false;
  private googleSignInButtonRef?: Ref;
  // Need a bounded function here since otherwise it'll lose "this"
  // context as a callback to the event listener below.
  private boundRender = () => this.render();

  connectedCallback() {
    document.addEventListener('thistle-signed-in', this.boundRender);
    document.addEventListener('thistle-signed-out', this.boundRender);
    this.googleSignInButtonRef = createRef();
    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener('thistle-signed-in', this.boundRender);
    document.removeEventListener('thistle-signed-out', this.boundRender);
  }

  private render() {
    if (authService.isAuthenticated()) {
      render(html``, this);
      return;
    }

    const renderGoogleSignInButton = () => {
      if ((window as any).google?.accounts?.id) {
        (window as any).google.accounts.id.initialize({
          client_id: this.dataset.googleClientId,
          callback: authService.handleGoogleCredentialResponse,
          hd: "thistle.co"
        });
        (window as any).google.accounts.id.renderButton(
          this.googleSignInButtonRef?.value,
          {
            type: "standard",
            shape: "rectangular",
            theme: "outline",
            text: "signin_with",
            size: "large",
            logo_alignment: "left",
          }
        );
      } else {
        this.shouldRenderGoogleSignInButton = true;
      }
    };

    // Set up a global callback that Google will call when ready
    (window as any).onGoogleLibraryLoad = () => {
      if (this.shouldRenderGoogleSignInButton) {
        renderGoogleSignInButton();
      }
    };

    const template = html`
      <div class="fixed inset-0 flex items-center justify-center z-50" style="background-color: rgba(0, 0, 0, 0.7)">
        <div class="bg-white rounded-lg p-6 shadow-lg">
          <div class="text-center">
            <h2 class="text-xl font-semibold mb-4 text-gray-800">Sign in to continue</h2>
            <div ${ref(this.googleSignInButtonRef)}></div>
            <div class="py-2">- or -</div>
            <div><button
              class="bg-emerald-500 hover:bg-emerald-400 px-6 py-2 rounded-md text-white font-semibold text-sm"
              onclick="window.location.reload()"
              >
                I'm in a Thistle Facility
              </button>
            </div>
          </div>
        </div>
      </div>
    `;

    render(template, this);

    // Attempt to render the sign in button if it hasn't been rendered yet
    if (this.googleSignInButtonRef?.value?.childElementCount === 0) {
      renderGoogleSignInButton();
    }
  }
}

// Register the custom element
customElements.define('auth-overlay', AuthOverlay);
